import { useCallback, useEffect, useState } from 'react';
import {
  listEntityTimeline,
  logTimelineEvent,
  timelineEventMeta,
  type CoreTimelineEvent,
} from '@/lib/coreTimeline';

interface Props {
  organizationId: number;
  tenantId: number | null;
  entityType: string;
  entityId: string | number;
  actorName?: string | null;
  actorUserId?: number | null;
  /** Masque le formulaire d'ajout (mode lecture seule). */
  readOnly?: boolean;
}

const INPUT =
  'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

function fmt(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function EntityTimelinePanel({
  organizationId,
  tenantId,
  entityType,
  entityId,
  actorName,
  actorUserId,
  readOnly,
}: Props) {
  const [events, setEvents] = useState<CoreTimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setEvents(await listEntityTimeline(organizationId, entityType, entityId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [organizationId, entityType, entityId]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await logTimelineEvent({
        tenantId,
        organizationId,
        entityType,
        entityId,
        eventType: 'custom',
        title: title.trim(),
        description: desc.trim() || null,
        actorName: actorName ?? null,
        actorUserId: actorUserId ?? null,
      });
      setTitle('');
      setDesc('');
      setOpen(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-background-200/70 bg-background-50 overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-background-200/60">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center">
            <i className="ri-history-line"></i>
          </span>
          <div>
            <h4 className="text-sm font-semibold text-foreground-900">Timeline</h4>
            <p className="text-[11px] text-foreground-400">Toutes les interactions liées à cet élément</p>
          </div>
        </div>
        {!readOnly && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className={open ? 'ri-close-line' : 'ri-add-line'}></i>
            {open ? 'Annuler' : 'Ajouter'}
          </button>
        )}
      </div>

      {open && !readOnly && (
        <div className="px-4 py-3 border-b border-background-200/60 bg-background-100/50 space-y-2">
          <input
            className={INPUT}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Intitulé (ex : Appel client, Relance envoyée…)"
          />
          <textarea
            className={`${INPUT} resize-none`}
            rows={2}
            maxLength={500}
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="Détail (optionnel)"
          ></textarea>
          <button
            type="button"
            onClick={add}
            disabled={saving || !title.trim()}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50"
          >
            {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
            Enregistrer dans la timeline
          </button>
        </div>
      )}

      {error && <div className="px-4 py-2.5 text-xs text-red-600 bg-red-50">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i>
        </div>
      ) : events.length === 0 ? (
        <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucune activité pour le moment.</p>
      ) : (
        <div className="px-4 py-3">
          <ol className="relative border-l border-background-200/70 ml-2">
            {events.map((ev) => {
              const meta = timelineEventMeta(ev.event_type);
              return (
                <li key={ev.id} className="ml-5 pb-4 last:pb-0">
                  <span className={`absolute -left-[13px] w-6 h-6 rounded-full flex items-center justify-center ${meta.className}`}>
                    <i className={`${ev.icon || meta.icon} text-[11px]`}></i>
                  </span>
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className="text-sm font-semibold text-foreground-900">{ev.title}</span>
                    {ev.amount != null && (
                      <span className="text-xs font-semibold text-primary-600">
                        {Number(ev.amount).toLocaleString()} {ev.currency || ''}
                      </span>
                    )}
                  </div>
                  {ev.description && <p className="text-xs text-foreground-600 mt-0.5">{ev.description}</p>}
                  <p className="text-[11px] text-foreground-400 mt-0.5">
                    {fmt(ev.occurred_at)}
                    {ev.actor_name ? ` · ${ev.actor_name}` : ''}
                  </p>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </div>
  );
}